import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import SearchState from './state';
import { SearchRequestResponse } from './types';

interface SearchPageLocationState {
  searchLogged?: boolean
}

/**
 * Logs a committed search when the results page is the entry point for it —
 * a bookmark, a shared link, or a hand-typed URL.
 *
 * Searches started from the search bar are logged there and arrive with
 * `searchLogged` set, so they are recorded once rather than twice. Facet and
 * paging changes re-issue the search request, but they refine an already
 * committed search, so they are never logged.
 */
export const useCommittedSearchLog = (
  searchState: SearchState,
  searchQuery: string,
  searchData: SearchRequestResponse | undefined,
): void => {
  const location = useLocation();
  const loggedQueries = useRef(new Set<string>());
  // The response being displayed when the query last changed. The new query's
  // own results have arrived once this identity changes, which is what keeps the
  // logged total from being taken off the previous query's response.
  const baselineData = useRef<SearchRequestResponse | undefined>(searchData);

  useEffect(() => {
    if ((location.state as SearchPageLocationState | undefined)?.searchLogged) {
      loggedQueries.current.add(searchQuery);
    }
    baselineData.current = searchData;
    // Deliberately keyed on the query alone: this captures the state of things
    // at the moment the query changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (!searchQuery || loggedQueries.current.has(searchQuery)) {
      return;
    }

    if (!searchData || searchData === baselineData.current) {
      return;
    }

    loggedQueries.current.add(searchQuery);
    searchState.logSearch({ searchText: searchQuery, total: searchData.total });
  }, [searchState, searchQuery, searchData]);
};
